import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardActions,
  Button,
  Typography,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import axios from "axios";
import { ROLE } from "../../api/role";

const modeOptions = ["Remote", "Onsite", "Hybrid"];
const typeOptions = ["Internship", "Full-Time", "Part-Time", "Contract"];
const categoryOptions = [
  "Developer",
  "Business Development",
  "Management",
  "Finance",
  "General",
];

const RoleOpeningPage = () => {
  const [roles, setRoles] = useState([]);
  const [open, setOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [editIndex, setEditIndex] = useState(null);
  const [deleteIndex, setDeleteIndex] = useState(null);
  const [formData, setFormData] = useState({
    role: "",
    category: "",
    mode: "",
    type: "",
    location: "",
    link: "",
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchRoles();
  }, []);

  const fetchRoles = async () => {
    try {
      const res = await axios.get(ROLE);
      setRoles(res.data);
    } catch (err) {
      console.error("Error fetching roles:", err);
    }
  };

  const handleOpen = (index = null) => {
    setEditIndex(index);
    if (index !== null) {
      setFormData(roles[index]);
    } else {
      setFormData({
        role: "",
        category: "",
        mode: "",
        type: "",
        location: "",
        link: "",
      });
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditIndex(null);
  };

  const handleDeleteOpen = (index) => {
    setDeleteIndex(index);
    setDeleteOpen(true);
  };

  const handleDeleteClose = () => {
    setDeleteOpen(false);
    setDeleteIndex(null);
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${ROLE}/${roles[deleteIndex]._id}`);
      fetchRoles();
      handleDeleteClose();
    } catch (err) {
      console.error("Error deleting role:", err);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const isValidUrl = (url) => {
    const pattern = new RegExp(
      "^(https?:\\/\\/)?" +
        "((([a-zA-Z\\d]([a-zA-Z\\d-]*[a-zA-Z\\d])*)\\.)+[a-zA-Z]{2,}|" +
        "((\\d{1,3}\\.){3}\\d{1,3}))" +
        "(\\:\\d+)?(\\/[-a-zA-Z\\d%_.~+]*)*" +
        "(\\?[;&a-zA-Z\\d%_.~+=-]*)?" +
        "(\\#[-a-zA-Z\\d_]*)?$",
      "i"
    );
    return !!pattern.test(url);
  };

  const handleSubmit = async () => {
    const { role, category, mode, type, location, link } = formData;
    let validationErrors = {};

    if (!role) validationErrors.role = "Role is required";
    if (!category) validationErrors.category = "Category is required";
    if (!mode) validationErrors.mode = "Mode is required";
    if (!type) validationErrors.type = "Type is required";
    if (!location) validationErrors.location = "Location is required";
    if (!link) {
      validationErrors.link = "Application link is required";
    } else if (!isValidUrl(link)) {
      validationErrors.link = "Please enter a valid URL";
    }

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    try {
      if (editIndex !== null) {
        await axios.put(`${ROLE}/${roles[editIndex]._id}`, formData);
      } else {
        await axios.post(ROLE, formData);
      }
      fetchRoles();
      handleClose();
    } catch (err) {
      console.error("Error saving role:", err);
    }
  };

  return (
    <div style={{ padding: "24px" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "24px",
        }}
      >
        <Typography variant="h5">Role Openings</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpen()}
        >
          Create Role
        </Button>
      </div>

      {roles.length === 0 ? (
        <Typography variant="body1">No roles added yet.</Typography>
      ) : (
        <Grid container spacing={3}>
          {roles.map((role, index) => (
            <Grid item xs={12} md={4} key={role._id}>
              <Card sx={{ borderRadius: "16px", boxShadow: 3, height: "100%" }}>
                <CardContent>
                  <Typography variant="h6">{role.role}</Typography>
                  <Typography variant="body2">
                    Category: {role.category}
                  </Typography>
                  <Typography variant="body2">Mode: {role.mode}</Typography>
                  <Typography variant="body2">Type: {role.type}</Typography>
                  <Typography variant="body2">
                    Location: {role.location}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: "blue", textDecoration: "underline" }}
                  >
                    <a href={role.link} target="_blank" rel="noreferrer">
                      Apply Here
                    </a>
                  </Typography>
                </CardContent>
                <CardActions>
                  <IconButton onClick={() => handleOpen(index)}>
                    <EditIcon />
                  </IconButton>
                  <IconButton onClick={() => handleDeleteOpen(index)}>
                    <DeleteIcon color="error" />
                  </IconButton>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle>
          {editIndex !== null ? "Edit Role" : "Add Role"}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Role"
            name="role"
            value={formData.role}
            onChange={handleChange}
            margin="dense"
            error={!!errors.role}
            helperText={errors.role}
          />

          <FormControl fullWidth margin="dense" error={!!errors.category}>
            <InputLabel>Category</InputLabel>
            <Select
              name="category"
              value={formData.category}
              onChange={handleChange}
            >
              {categoryOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth margin="dense" error={!!errors.mode}>
            <InputLabel>Mode</InputLabel>
            <Select name="mode" value={formData.mode} onChange={handleChange}>
              {modeOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth margin="dense" error={!!errors.type}>
            <InputLabel>Type</InputLabel>
            <Select name="type" value={formData.type} onChange={handleChange}>
              {typeOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label="Location"
            name="location"
            value={formData.location}
            onChange={handleChange}
            margin="dense"
            error={!!errors.location}
            helperText={errors.location}
          />

          <TextField
            fullWidth
            label="Application Link"
            name="link"
            value={formData.link}
            onChange={handleChange}
            margin="dense"
            error={!!errors.link}
            helperText={errors.link}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit}>
            {editIndex !== null ? "Update" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteOpen} onClose={handleDeleteClose}>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          Are you sure you want to delete this role?
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteClose}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default RoleOpeningPage;
